import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  useTheme,
} from '@mui/material';

import {
  Schedule,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { shiftApi } from '../../services/api';
import { Shift } from '../../types';
import {
  PageHeader,
} from '../../components/SharedPageParts';

const ShiftsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

  const handleSubmit = async (shift: Shift) => {
    const data = await shiftApi.createUpdateShift(shift);
    if (shift.id) {
      alert('Shift timing updated!');
    } else {
      alert('Shift timing created!');
    }
    setOpen(false);
    loadShifts(); // reload after creation
  };

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadShifts();
  }, []);

  const loadShifts = async () => {
    try {
      setLoading(true);
      setError(null);
      const shiftsData = await shiftApi.getShifts();
      setShifts(shiftsData);
    } catch (err) {
      setError('Failed to load shifts. Make sure the backend is running on port 8081.');
      console.error('Error loading shifts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredShifts = shifts.filter(
    (shift) =>
      shift.shiftName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto'}}>
      <PageHeader
                title="Shift Timings Management"
                Icon={Schedule}
                onBack={() => navigate('/commondata')}
                backLabel="Back to Common Data"
                subtitle="Manage shift schedules and durations."
              />

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: '1px solid #ef4444',
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search shifts by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary },
              }}
              sx={{
                flexGrow: 1,
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
                '& .MuiInputBase-input': { color: theme.palette.text.primary },
              }}
            />
            {/* <Button
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)',
                },
                mt: 1,
              }}
              onClick={() => {setOpen(true)
              setSelectedShift(null);
              }}
            >
              Add Shift Timing
            </Button>
            <ShiftFormModel open={open} onClose={() => setOpen(false)} onSubmit={loadShifts} shift={selectedShift}/> */}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Shifts ({filteredShifts.length})
          </Typography>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Start Time</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>End Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredShifts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Schedule sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                          No shifts found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {searchTerm
                            ? 'Try adjusting your search terms'
                            : 'Start the backend to load shifts'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredShifts.map((shift) => (
                    <TableRow
                      key={shift.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: theme.palette.background.paper },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <TableCell sx={{ borderBottom: 'none', color: theme.palette.text.primary }}>
                        {shift.shiftCode}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', color: theme.palette.text.primary }}>
                        {shift.shiftName}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', color: theme.palette.text.primary }}>
                        {shift.startTime}
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', color: theme.palette.text.primary }}>
                        {shift.endTime}
                      </TableCell>
                      {/* <TableCell align="center" sx={{ borderBottom: 'none' }}>
                        <IconButton size="small" sx={{ color: '#06beb6' }} onClick={() => {
                                setSelectedShift(shift);  // pass the row data
                                setOpen(true);
                              }}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: '#ef4444' }}
                          onClick={() => shiftApi.deleteShift(shift.id).then(loadShifts)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell> */}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};
export default ShiftsPage;       